import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Drawer,
  CircularProgress,
  Alert,
  Paper,
  ButtonGroup,
  useTheme,
} from '@mui/material';
import { Plus, Sheet, Square } from 'lucide-react';
import { useResources } from '../../hooks/resource/useResources';
import { useResourceActions } from '../../hooks/resource/useResourceActions';
import ResourceTable from '../../components/resources/ResourceTable';
import ResourceForm from '../../components/resources/ResourceForm';
import ResourceDetail from './ResourceDetail';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { Resource, CreateResourceDTO } from '../../types/resource';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';
import { NotificationT, SEVERITY } from '../../types/notification';
import EmptyStateIcon from '../../assets/images/emptyState-illus.svg';
import ResourceCard from '../../components/resources/ResourceCard';

const ResourcesView = () => {
  const theme = useTheme();
  const {
    resources,
    isLoading: isLoadingResources,
    error: resourcesError,
    refetch,
  } = useResources();
  const {
    createResource,
    updateResource,
    deleteResource,
    isLoading: isActionLoading,
    error: actionError,
  } = useResourceActions();

  // UI State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTableView, setIsTableView] = useState(true);
  const { role } = useAuth();

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    resourceId: 0,
  });

  // Notification State
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const showNotification = (message: string, severity: SEVERITY) => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleAdd = () => {
    setSelectedResource(null);
    setIsFormOpen(true);
  };

  const handleEdit = (resource: Resource) => {
    setSelectedResource(resource);
    setIsFormOpen(true);
    setIsDetailOpen(false);
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({
      open: true,
      resourceId: id,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteResource(confirmDialog.resourceId);
      await refetch();
      showNotification('Staff member deleted successfully', SEVERITY.SUCCESS);
      setIsDetailOpen(false);
    } catch (error) {
      showNotification('Failed to delete staff member', SEVERITY.ERROR);
    } finally {
      setConfirmDialog({ open: false, resourceId: 0 });
    }
  };

  const handleSubmit = async (data: CreateResourceDTO) => {
    setIsSubmitting(true);
    try {
      if (selectedResource) {
        await updateResource(selectedResource.id, data);
        showNotification('Staff member updated successfully', SEVERITY.SUCCESS);
      } else {
        await createResource(data);
        showNotification('Staff member created successfully', SEVERITY.SUCCESS);
      }
      await refetch();
      setIsFormOpen(false);
      setSelectedResource(null);
    } catch (error) {
      showNotification(
        `Failed to ${selectedResource ? 'update' : 'create'} staff member`,
        SEVERITY.ERROR
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelect = (resource: Resource) => {
    setSelectedResource(resource);
    setIsDetailOpen(true);
  };

  if (resourcesError || actionError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{resourcesError || actionError}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Staff
        </Typography>
        {role !== AUTH_CONSTANTS.ROLES.USER && resources.length ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              disableElevation
              startIcon={<Plus size={20} />}
              onClick={handleAdd}
              className="resources-tour"
            >
              Add Staff Member
            </Button>
            <ButtonGroup
              size="small"
              sx={{
                '& .MuiButton-root': {
                  textTransform: 'none',
                  height: 36,
                  px: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  '& .MuiButton-startIcon': {
                    m: 0,
                  },
                },
              }}
            >
              <Button
                onClick={() => setIsTableView(true)}
                disableElevation
                variant="text"
                sx={{ color: isTableView ? 'primary.main' : theme.palette.grey[500] }}
              >
                <Sheet size={21} />
              </Button>
              <Button
                onClick={() => setIsTableView(false)}
                disableElevation
                variant="text"
                sx={{ color: isTableView ? theme.palette.grey[500] : 'primary.main' }}
              >
                <Square size={21} />
              </Button>
            </ButtonGroup>
          </Box>
        ) : null}
      </Box>

      {/* <Paper elevation={0} > */}
      {isLoadingResources || isActionLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : !resources.length ? (
        <Paper elevation={0}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, py: 6 }}
          >
            <img className="w-28 mx-auto" src={EmptyStateIcon} alt="No data found" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              No staff members found
            </Typography>
            <Button
              variant="contained"
              disableElevation
              startIcon={<Plus size={20} />}
              onClick={handleAdd}
              sx={{ mt: 3 }}
              className="resources-tour"
            >
              Add Staff Member
            </Button>
          </Box>
        </Paper>
      ) : isTableView ? (
        <ResourceTable
          resources={resources}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onSelect={handleSelect}
        />
      ) : (
        <ResourceCard
          resources={resources}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onSelect={handleSelect}
        />
      )}
      {/* </Paper> */}

      <Drawer
        anchor="right"
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '70%', md: '50%', lg: '40%' },
            p: 0,
            py: { xs: 2, sm: 2 },
            px: { xs: 2, sm: 3 },
          },
        }}
      >
        <ResourceForm
          resource={selectedResource || undefined}
          isEdit={!!selectedResource}
          onSubmit={handleSubmit}
          onClose={() => setIsFormOpen(false)}
          isSubmitting={isSubmitting}
        />
      </Drawer>

      <Drawer
        anchor="right"
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '70%', xl: '50%' },
            p: 0,
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2 },
          },
        }}
      >
        {selectedResource && (
          <ResourceDetail
            resource={selectedResource}
            onClose={() => setIsDetailOpen(false)}
            onEdit={() => {
              setIsDetailOpen(false);
              handleEdit(selectedResource);
            }}
            onDelete={() => handleDeleteClick(selectedResource.id)}
          />
        )}
      </Drawer>

      <ConfirmationDialog
        open={confirmDialog.open}
        title="Delete Staff Member"
        message="Are you sure you want to delete this staff member? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, resourceId: 0 })}
      />

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ResourcesView;
